import type { ThemeFontSpec } from "./ThemeManifest.js";

/** document.fonts 중 사용하는 부분만의 구조 타입 — 단위테스트 mock 주입용. */
export interface FontRegistry {
  add(face: FontFace): unknown;
  readonly ready: Promise<unknown>;
}

/** 테스트 주입용 환경 — 미지정 시 브라우저 전역(FontFace/document.fonts)을 쓴다. */
export interface FontLoaderEnv {
  readonly createFontFace?: (
    family: string,
    source: string,
    descriptors?: { weight?: string },
  ) => FontFace;
  readonly fonts?: FontRegistry;
}

/**
 * 테마 폰트를 FontFace API로 로드해 document.fonts에 등록한다 — index.html @font-face 없이
 * 매니페스트가 폰트 SSoT가 된다. Phaser canvas Text는 CSS 폰트 로드를 자동 대기하지 않으므로
 * Game 생성 전에 await할 것. 실패해도 throw하지 않고 family 문자열의 fallback으로 렌더하게 둔다.
 */
export async function loadThemeFont(font: ThemeFontSpec, env?: FontLoaderEnv): Promise<void> {
  try {
    const create =
      env?.createFontFace ??
      ((family, source, descriptors) => new FontFace(family, source, descriptors));
    // TS DOM lib의 FontFaceSet 선언에는 add가 없지만 모든 브라우저 런타임에 존재 — 구조 타입으로 단언.
    const fonts: FontRegistry = env?.fonts ?? (document.fonts as unknown as FontRegistry);
    const face = create(font.family, `url("${font.file}")`, { weight: font.weight ?? "normal" });
    await face.load();
    fonts.add(face);
    await fonts.ready;
  } catch {
    /* 폰트 로드 실패 시 fallback(sans-serif 등)으로 진행 — 부팅을 막지 않는다 */
  }
}
